/**
 * Active location vs saved address.
 *
 * `userSetAddress` answers "where am I browsing from" and can come from GPS, a
 * default city, or a real address book entry. Only the last one is a delivery
 * address, so checkout must check the source instead of just the coordinates.
 */

import {
  checkGeolocationPermission,
  getCurrentGPSLocation,
  isSecureContext,
  reverseGeocode,
} from "../utils/locationUtils";

export const ACTIVE_LOCATION_KEY = "userSetAddress";
export const LOCATION_SOURCE_KEY = "activeLocationSource";

export const LocationSource = {
  SAVED: "saved",
  GPS: "gps",
  DEFAULT: "default",
};

/** Fallback service area when GPS is denied or unavailable (Bengaluru). */
export const DEFAULT_CITY_LOCATION = {
  lat: "12.9716",
  lng: "77.5946",
  address: "Bengaluru, Karnataka, India",
  house: null,
  tag: null,
};

export const getActiveLocation = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.lat && parsed.lng ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const hasActiveLocation = () => !!getActiveLocation();

/**
 * Locations stored before this key existed are treated as saved addresses,
 * otherwise existing users would suddenly be blocked at checkout.
 */
export const getLocationSource = () => {
  if (!hasActiveLocation()) return null;
  try {
    return localStorage.getItem(LOCATION_SOURCE_KEY) || LocationSource.SAVED;
  } catch (e) {
    return LocationSource.SAVED;
  }
};

export const isSavedAddressActive = () =>
  getLocationSource() === LocationSource.SAVED;

export const setActiveLocation = (location, source) => {
  if (!location || location.lat == null || location.lng == null) return null;

  const lat = String(location.lat);
  const lng = String(location.lng);
  const stored = {
    ...location,
    lat,
    lng,
    house: location.house != null ? location.house : null,
    tag: location.tag != null ? location.tag : null,
  };

  try {
    localStorage.setItem(ACTIVE_LOCATION_KEY, JSON.stringify(stored));
    localStorage.setItem("userLat", lat);
    localStorage.setItem("userLng", lng);
    localStorage.setItem(
      "geoLocation",
      JSON.stringify({
        formatted_address: stored.address || "",
        geometry: { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
      })
    );
    localStorage.setItem(LOCATION_SOURCE_KEY, source || LocationSource.GPS);
  } catch (e) {
    return null;
  }

  return stored;
};

/** Promote whatever is active to a delivery address (called after a real save). */
export const markActiveLocationSaved = () => {
  try {
    localStorage.setItem(LOCATION_SOURCE_KEY, LocationSource.SAVED);
  } catch (e) {
    // ignore
  }
};

/** Address-book row (backend shape) → active location. */
export const setActiveLocationFromSavedAddress = (address) => {
  if (!address) return null;
  const lat = address.latitude != null ? address.latitude : address.lat;
  const lng = address.longitude != null ? address.longitude : address.lng;
  if (lat == null || lng == null) return null;

  return setActiveLocation(
    {
      id: address.id,
      lat,
      lng,
      address: address.address,
      house: address.house,
      tag: address.tag,
      building_block: address.building_block,
      landmark_area: address.landmark_area,
      receiver_name: address.receiver_name,
      receiver_phone: address.receiver_phone,
    },
    LocationSource.SAVED
  );
};

/**
 * Resolve GPS without showing anything, but only when the OS permission was
 * already granted — asking for it needs a user gesture on iOS.
 * @returns {Promise<Object|null>} the stored location, or null if we must ask.
 */
export const detectActiveLocationSilently = async () => {
  try {
    if (!isSecureContext()) return null;

    const permission = await checkGeolocationPermission();
    if (permission !== "granted") return null;

    const coords = await getCurrentGPSLocation();
    if (!coords || coords.lat == null || coords.lng == null) return null;

    let address = "";
    try {
      const geo = await reverseGeocode(coords.lat, coords.lng);
      address = (geo && geo.formatted_address) || "";
    } catch (e) {
      // Coordinates are enough to browse; the label can stay empty.
    }

    return setActiveLocation(
      { lat: coords.lat, lng: coords.lng, address },
      LocationSource.GPS
    );
  } catch (e) {
    return null;
  }
};
